<template>
	<div id="vx-col w-full mb-base" ref="popupContainer">
		<!-- Form Fields Section -->
		<div class="form-section mb-6">
			<div class="vx-row">
				<div class="flex">
					<!-- Keyword Input -->
					<div class="mr-5">
						<label class="text-base font-semibold mb-2">Mot-clé *</label>
						<vs-input
							v-model="formData.keyword"
							placeholder="Entrez votre mot-clé"
							class="w-full"
						/>
					</div>

					<!-- Location and Language Dropdowns Row -->
					<div
						style="
							display: flex;
							flex-direction: row;
							justify-content: space-between;
						"
					>
						<!-- Location Dropdown -->
						<vs-select
							v-if="$acl.check('sadmin')"
							autocomplete
							:key="countryListKey"
							label="Localisation *"
							v-model="formData.location_id"
							class="w-2/4 mr-5"
							placeholder="Sélectionnez un pays"
						>
							<vs-select-item
								:key="index"
								:value="item.id"
								:text="item.location_name"
								v-for="(item, index) in country_list"
								class="w-full"
							/>
						</vs-select>

						<!-- Language Dropdown -->
						<vs-select
							:key="languageListKey"
							v-if="$acl.check('sadmin')"
							autocomplete
							label="Langue *"
							v-model="formData.language_id"
							class="w-2/4"
							placeholder="Sélectionnez une langue"
						>
							<vs-select-item
								:key="index"
								:value="item.id"
								:text="item.name"
								v-for="(item, index) in search_engines_languages"
								class="w-full"
							/>
						</vs-select>
					</div>

					<!-- Generate Button -->
					<vs-button
						class="mt-6 ml-5"
						@click="generateTemplate()"
						:disabled="!canGenerate"
					>
						<span v-if="isGenerating">Generating...</span>
						<span v-else>Generate</span>
					</vs-button>
				</div>
			</div>
		</div>

		<!-- Alert for operation in progress -->
		<div v-if="hasOperationInProgress" class="mt-3 p-3">
			<vs-alert active="true" color="warning">
				<div class="flex items-center">
					<svg
						class="w-4 h-4 text-orange-600 mr-2"
						fill="currentColor"
						viewBox="0 0 20 20"
					>
						<path
							fill-rule="evenodd"
							d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
							clip-rule="evenodd"
						></path>
					</svg>
					<span>
						Attention : Le processus peut durer plusieurs minutes, voire 10
						minutes. Vous pouvez fermer la fenêtre et revenir peu de temps
						après.
					</span>
				</div>
			</vs-alert>
		</div>

		<!-- STG Table -->
		<vx-card class="mt-5">
			<div class="mt-5">
				<h3 class="text-lg font-semibold mb-4">SEO Template Generations</h3>
				<div v-if="isLoading" class="text-center py-4">Loading...</div>
				<div
					v-else-if="stgData.length === 0"
					class="text-center py-4 text-gray-500"
				>
					No data available
				</div>
				<table v-else class="w-full border-collapse border border-gray-300">
					<thead>
						<tr class="bg-gray-100">
							<th class="border border-gray-300 px-4 py-2 text-left">ID</th>
							<th class="border border-gray-300 px-4 py-2 text-left">
								Mot-clé
							</th>
							<!-- <th class="border border-gray-300 px-4 py-2 text-left">
								Localisation
							</th>
							<th class="border border-gray-300 px-4 py-2 text-left">Langue</th> -->
							<th class="border border-gray-300 px-4 py-2 text-left">URL</th>
							<th class="border border-gray-300 px-4 py-2 text-left">Statut</th>
							<th class="border border-gray-300 px-4 py-2 text-left">Date</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="item in stgData" :key="item.id" class="hover:bg-gray-50">
							<td class="border border-gray-300 px-4 py-2">{{ item.id }}</td>
							<td class="border border-gray-300 px-4 py-2">
								{{ item.keyword }}
							</td>
							<!-- <td class="border border-gray-300 px-4 py-2">
								{{ getLocationName(item.location_id) }}
							</td>
							<td class="border border-gray-300 px-4 py-2">
								{{ getLanguageName(item.language_id) }}
							</td> -->
							<td class="border border-gray-300 px-4 py-2">
								<a
									v-if="item.url"
									:href="item.url"
									target="_blank"
									class="text-blue-600 hover:text-blue-800 underline"
								>
									{{ truncateUrl(item.url) }}
								</a>
								<span v-else class="text-gray-400">No URL</span>
							</td>
							<td class="border border-gray-300 px-4 py-2">
								<div class="flex items-center">
									<div
										v-if="item.status === 0"
										class="flex items-center text-orange-600"
									>
										<div
											class="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"
										></div>
										<circular-loader />
									</div>
									<div v-else class="flex items-center">
										{{ getStatusName(item.status) }}
									</div>
								</div>
							</td>
							<td class="border border-gray-300 px-4 py-2">
								{{ formatDate(item.created_date) }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</vx-card>
	</div>
</template>

<script>
import dbFieldConvertor from "@/mixins/dbFieldConvertorMixin";
import utilsMixin from "@/mixins/utilsMixin";
import store from "@/store/store";
import VueCookies from "vue-cookies";
import CircularLoader from "../../components/widgets/loader/CircularLoader.vue";
import ProspectSearch from "@/entities/ProspectSearch"; // Import for API calls

export default {
	components: { CircularLoader },
	name: "SeoTemplateGeneration",
	mixins: [dbFieldConvertor, utilsMixin],
	data() {
		return {
			apiUrl: this.$store.state.backendApiUrl,
			formData: {
				keyword: "",
				location_id: 72,
				language_id: 32,
			},
			stgData: [],
			isLoading: false,
			isGenerating: false,
			// Dropdown data arrays (following Code 1 pattern)
			country_list: [],
			search_engines_languages: [],
			// Keys for reactive re-rendering (following Code 1 pattern)
			countryListKey: 0,
			languageListKey: 0,
		};
	},
	created() {
		this.fetchSTGData();
		// Fetch dropdown data (following Code 1 pattern)
		this.fetchSearchEnginesCountries(1); // Default search engine ID
		this.fetchSearchEnginesLanguages(1); // Default search engine ID
	},
	mounted() {},
	computed: {
		activeUser: () => {
			return VueCookies.get("userInfo");
		},
		hasOperationInProgress() {
			return this.stgData.some((item) => item.status === 0);
		},
		canGenerate() {
			return (
				this.formData.keyword.trim() &&
				this.formData.location_id &&
				this.formData.language_id &&
				!this.isGenerating
			);
		},
	},
	watch: {
		// Watch for location changes (following Code 1 pattern)
		"formData.location_id": function (val) {
			console.log("Location selected:", val);
		},
		// Watch for language changes (following Code 1 pattern)
		"formData.language_id": function (val) {
			console.log("Language selected:", val);
		},
	},

	methods: {
		// Fetch dropdown data methods (adapted from Code 1)
		fetchSearchEnginesCountries(searchEngineId = 1) {
			ProspectSearch.getSearchEnginesCountryList(searchEngineId, (err, res) => {
				if (!err && res) {
					this.country_list = res;
					this.countryListKey++;
				}
			});
		},

		fetchSearchEnginesLanguages(searchEngineId = 1) {
			ProspectSearch.getSearchEnginesLanguages(searchEngineId, (err, res) => {
				if (!err && res) {
					this.search_engines_languages = res;
					this.languageListKey++;
				}
			});
		},

		// Helper methods to get names for display in table
		getLocationName(locationId) {
			if (!locationId || !this.country_list.length) return "N/A";
			const location = this.country_list.find((item) => item.id === locationId);
			return location ? location.location_name : "N/A";
		},
		getLocationCode(locationId) {
			if (!locationId || !this.country_list.length) return "N/A";
			const location = this.country_list.find((item) => item.id === locationId);
			return location ? location.location_code : "N/A";
		},

		getLanguageName(languageId) {
			if (!languageId || !this.search_engines_languages.length) return "N/A";
			const language = this.search_engines_languages.find(
				(item) => item.id === languageId
			);
			return language ? language.name : "N/A";
		},
		getLanguageCode(languageId) {
			if (!languageId || !this.search_engines_languages.length) return "N/A";
			const language = this.search_engines_languages.find(
				(item) => item.id === languageId
			);
			return language ? language.code : "N/A";
		},

		// Fetch STG data from API
		fetchSTGData() {
			this.isLoading = true;
			this.$http
				.get(this.apiUrl + "others/stg")
				.then((result) => {
					// Sort by created_date descending (most recent first)
					this.stgData = result.data.sort((a, b) => {
						return new Date(b.created_date) - new Date(a.created_date);
					});
					this.isLoading = false;
				})
				.catch((err) => {
					console.error("Error fetching STG data:", err);
					this.isLoading = false;
				});
		},

		getStatusName(status) {
			switch (status) {
				case 0:
					return "En cours";
				case 1:
					return "Terminé";
				case 2:
					return "En attente";
				case 3:
					return "Échoué";
				default:
					return "Inconnu";
			}
		},

		// Generate new template (updated to include location and language)
		generateTemplate() {
			if (!this.canGenerate) {
				this.$vs.notify({
					title: "Erreur",
					text: "Veuillez remplir tous les champs requis",
					color: "danger",
				});
				return;
			}

			this.isGenerating = true;

			const payload = {
				keyword: this.formData.keyword.trim(),
				localisation: {
					location_id: this.formData.location_id || 72,
					language_id: this.formData.language_id || 32,
					location_name:
						this.getLocationName(this.formData.location_id) || "France",
					language_name:
						this.getLanguageName(this.formData.language_id) || "French",
					language_code:
						this.getLanguageCode(this.formData.language_id) || "fr",
					location_code:
						this.getLocationCode(this.formData.location_id) || 2250, // Assuming location_id is the code
				},
				status: 2, // Start with waiting status
				url: null, // Will be populated when processing is done
			};

			this.$http
				.post(this.apiUrl + "others/stg", payload)
				.then((result) => {
					// Clear the form data
					this.formData = {
						keyword: "",
						location_id: 72,
						language_id: 32,
						location_name: "",
						language_name: "",
						language_code: "",
						location_code: "",
					};

					// Reload the table data
					this.fetchSTGData();

					this.isGenerating = false;

					// Show success message
					this.$vs.notify({
						title: "Succès",
						text: "Génération de template démarrée",
						color: "success",
					});
				})
				.catch((err) => {
					console.error("Error generating template:", err);
					this.isGenerating = false;

					// Show error message
					this.$vs.notify({
						title: "Erreur",
						text: "Échec de la génération de template",
						color: "danger",
					});
				});
		},

		// Format date for display
		formatDate(dateString) {
			if (!dateString) return "";
			const date = new Date(dateString);
			return date.toLocaleString();
		},

		// Truncate URL for better display
		truncateUrl(url) {
			if (!url) return "";
			if (url.length <= 50) return url;
			return url.substring(0, 47) + "...";
		},

		// Refresh data (can be called manually)
		refreshData() {
			this.fetchSTGData();
		},
	},
};
</script>

<style scoped>
.center {
	justify-content: center;
}

.form-section {
	background: #f8f9fa;
	padding: 20px;
	border-radius: 8px;
	margin-bottom: 20px;
}

/* Table styles */
table {
	font-size: 14px;
}

/* Loading animation */
@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.animate-spin {
	animation: spin 1s linear infinite;
}

/* Link hover effects */
a:hover {
	text-decoration: underline;
}

/* Button disabled state */
button:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

/* Dropdown styling to match Code 1 */
.vs-select {
	margin-bottom: 10px;
}
</style>